import { useState, useEffect, useRef } from 'react';
import { Button } from '@safetyhub/ui';
import { adminApi, extractApiError, type ReleaseNotePlatform } from '../lib/api';
import { useToastStore } from '../stores/toast';
import ReleaseNoteAudienceModal from '../components/ReleaseNoteAudienceModal';
import { useAudienceOptions, audienceLabel } from '../lib/audience-options';
import {
  CATEGORY_COLORS,
  CATEGORY_LABELS,
  PLATFORM_LABELS,
  platformColor,
  platformLabel,
  toKSTDateString,
  type Category,
} from '../lib/releaseNoteDisplay';
import { withDirectionParticle } from '@safetyhub/shared';

/**
 * surface·분류 타입과 **표시 어휘**(라벨·색·날짜 변환)는 `lib/releaseNoteDisplay.ts`가 소유한다 —
 * **소비 뷰(`ReleaseNoteFeed.tsx`)와 공용**이다(2026-10-21 결정 채택 ⑥).
 *
 * ⚠️ 여기 사본을 되살리지 말 것 — 표가 두 벌이면 두 화면의 같은 surface가 다른 이름·다른 색으로
 * 보이는데 **그 갈라짐에는 red가 없다**(사유 전문은 그 파일 헤더). 반대로 **아래 남아 있는 것들
 * (`LEGACY_SURFACES`·`PLATFORM_FILTER_OPTIONS`·`STATUS_*`·`EMPTY_PLATFORMS_*`)을 그쪽으로 올리지도
 * 말 것** — 그 JSDoc들의 근거가 **이 화면 고유**다(같은 파일 헤더의 「옮기지 않은 것」 절).
 */
type Platform = ReleaseNotePlatform;

/**
 * 목록이 **표시**하는 status — 세 값 그대로다(옛 `DRAFT` 행이 남아 있을 수 있다).
 *
 * ⚠️ **쓸 수 있는 값과 다르다** — 전환 요청에 실을 수 있는 것은 {@link VisibilityStatus} 둘뿐이다.
 */
type Status = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

/**
 * 전환 요청에 실을 수 있는 status — 숨김/복구 둘뿐이다.
 *
 * 서버 DTO가 `@IsIn([PUBLISHED, ARCHIVED])`라 `DRAFT`를 보내면 **400**이다(사유는 서버
 * `RELEASE_NOTE_VISIBILITY_STATUSES` JSDoc).
 */
type VisibilityStatus = 'PUBLISHED' | 'ARCHIVED';

/**
 * 목록 행이 이 화면에 들어오는 계약 — **export는 spec 픽스처의 «타입 앵커»를 위해서다.**
 *
 * `__tests__/releaseNotesPlatformChips.test.tsx`의 `noteRow`가 반환을 이 타입으로 선언하므로,
 * 여기에 칸이 늘면 **그 픽스처가 tsc에서 깨진다**. 그 앵커가 없던 2026-10-19에 아래 `audiences`가
 * 추가되고 픽스처가 따라오지 않아 렌더가 `undefined.length`로 죽었다 — 그 spec은 `vi.mock`으로
 * `lib/api`를 통째로 바꾸므로 **응답 타입과의 연결이 끊겨 tsc·eslint가 전부 조용했고**, 그 커밋이
 * 미push라 CI도 못 봐서 **배포가 멈춘 자리에서 처음 터졌다**(5케이스 전부 red).
 * export를 지우려면 그 앵커를 먼저 다른 자리로 옮길 것.
 */
export interface ReleaseNote {
  id: string;
  title: string;
  content: string | null;
  /**
   * surface **집합** — 판정의 정본이고 **칩이 읽는 칸**이다(서버 `release_notes.platforms`).
   *
   * ⚠️ 응답에는 처음부터 있었다 — 빠져 있던 것은 `lib/api.ts`의 손열거 타입이었고, 그래서 이 화면이
   * 아래 `platform`(대표값)으로 칩을 그렸다. **목록 필터는 집합 축**(서버 `platforms: { has: … }`)
   * 이므로 둘이 어긋나 운영자에게 「필터와 안 맞는 행」으로 읽혔다(prod 실측 `ADMIN` 축 **108건** —
   * 분모 1506행, 서버 `release-notes.service.ts` `findAll` 주석).
   */
  platforms: Platform[];
  /**
   * ⚠️ **대표값이다 — 표시에 읽지 말 것**(`platforms`를 읽는다). 불변식은 `sorted(platforms)[0]`
   * (`schema.prisma` 컬럼 주석). 서버가 여전히 돌려주므로 선언에서 빼지 않는다.
   */
  platform: Platform;
  /**
   * 노출 대상 역할 집합 — `<네임스페이스>:<역할>`. 규약·판정의 정본은 shared
   * `features/audience-targeting.ts`이고 편집은 {@link ReleaseNoteAudienceModal}이 한다.
   *
   * ⚠️ **빈 배열의 뜻이 `platforms`와 «반대»다**: 여기서 빈 배열은 **「모두에게」**(정상·기본값)이고
   * `platforms`의 빈 배열은 **결함**이다(그래서 그쪽만 빨간 `(없음)` 칩을 띄운다). 두 칸을 같은
   * 처분으로 그리지 말 것 — 그러면 1500여 건 전량에 결함 표시가 붙는다.
   */
  audiences: string[];
  category: Category;
  status: Status;
  releasedAt: string;
  version: string | null;
  commitHash: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * **새로 쓰지 않는 surface 값** — 필터 옵션에서 빼는 집합이다.
 *
 * ## 파생이 아니라 **선언**이다 — 그래서 사본이고, 그래서 래칫이 짝이다
 * 「어느 값을 더는 쓰지 않을지」는 **정책 판단**이라 코드에서 파생할 수 없다(enum 값은 일곱 다
 * 유효하고, 그중 셋을 접기로 한 것은 사람의 결정이다). **정본은 `apps/server/prisma/schema.prisma`의
 * `enum ReleaseNotePlatform` 멤버 주석에 붙은 「⚠️ 옛 값 … (새로 쓰지 않는다)」 표식**이고, admin은
 * 브라우저 번들이라 그 파일도 `scripts/release-notes/*`도 **런타임에 읽을 수 없어** 손으로 베낀다.
 *
 * ⚠️ 그래서 이 상수가 갈리는 것을 **기계가 잰다**:
 * `scripts/release-notes/frontend-surface-copies.test.mjs`의 「프론트 «옛 값» 사본」 축이 이 선언을
 * 스캔해 enum 표식과 **양방향**으로 대조한다(한쪽만 고치면 red다). 그 검사를 고른 이유는 CI 경로
 * 조건이다 — `script-tests` job은 경로 조건이 **없어 매 run 돌지만** admin vitest는 admin 경로가
 * 안 바뀌면 skip이라, **`schema.prisma`만 바꾼 push에서 정확히 안 돈다.**
 * ⇒ 이 목록을 고치려면 enum 주석·`surface-values.test.mjs`의 `LEGACY_SURFACES`와 **함께** 고치고,
 * 정책을 바꾸는 것이면 결정 기록도 함께 고쳐라.
 *
 * ## 세 값의 사유 — 한 문장으로 뭉치면 다음 사람이 잘못 되돌린다
 * (결정 `docs/decisions/2026-10-15-release-note-surfaces-as-a-set-and-popup-decisions.md` §A)
 * - `ALL`: 걷어낸 「전부」 태그다. **집합 축에서 `ALL`은 와일드카드가 아니라 원소**라, 고르면
 *   `platforms: { has: 'ALL' }`로 조회되어 **목록이 빈다**. 하필 바로 위 「모든 플랫폼」(필터 없음)과
 *   한국어로 구별되지 않아 **가장 나쁜 옵션**이었다.
 * - `MOBILE`: `APP`의 전신. 재분류로 데이터가 0건이지만 **옛 캐시 번들이 아직 보내는 값**이라
 *   enum에는 남는다(그 사유는 enum 주석이 정본).
 * - `SERVER`: 「서버/API」는 애초에 surface가 아니었다. 430건을 재분류해 비웠다.
 *
 * `INTERNAL`은 **빼지 않는다** — 공개 피드에서만 서버가 배제하고(결정
 * `2026-10-15-internal-is-an-explicit-empty-surface-and-the-server-owns-its-enforcement.md`)
 * admin에서는 그 축으로 목록을 봐야 한다(prod 실측 2026-10-16 **145건**).
 *
 * ## ⚠️ enum·`schema.json`·래칫에서 이 셋을 «지우지» 말 것
 * PG는 enum 값 제거를 지원하지 않아 타입 재생성(destructive)이 필요하고, 위 `MOBILE` 사유가 남긴다.
 * 좁히는 것은 **이 화면의 옵션**뿐이다.
 */
const LEGACY_SURFACES: readonly Platform[] = ['ALL', 'MOBILE', 'SERVER'];

/**
 * 필터 드롭다운의 옵션 — `lib/releaseNoteDisplay.ts`의 {@link PLATFORM_LABELS}에서
 * {@link LEGACY_SURFACES}를 **뺀 차집합**이다.
 *
 * 남는 값을 손열거하지 않는 이유: 그러면 **유산 정책이 바뀔 때 함께 움직이지 않는다.** 유산 셋에서
 * 값을 더하거나 빼면(또는 enum에 여덟째 값이 생기면) 이 목록이 자동으로 따라온다.
 *
 * prod 실측(2026-10-16, 단수 칼럼·집합 칼럼 **둘 다**): 쓰이는 값은 `APP` 1152 · `WEB` 1149 ·
 * `ADMIN` 321 · `INTERNAL` 145이고 **`ALL`·`MOBILE`·`SERVER`는 0건**이었다 — 즉 뺀 셋은 고르면
 * 언제나 빈 목록을 주는 옵션이었다.
 *
 * 탐지기: `__tests__/releaseNotesPlatformChips.test.tsx`의 「필터 옵션」 describe. **「유산 셋이 없다」만
 * 단언하면 목록을 통째로 잃어도 통과**하므로 `[대조군]`으로 「오늘의 네 값은 있다」를 짝지었다.
 */
const PLATFORM_FILTER_OPTIONS: readonly [Platform, string][] = (
  Object.entries(PLATFORM_LABELS) as [Platform, string][]
).filter(([value]) => !LEGACY_SURFACES.includes(value));

const STATUS_LABELS: Record<Status, string> = {
  DRAFT: '초안',
  PUBLISHED: '게시됨',
  ARCHIVED: '비공개',
};

/**
 * surface 집합이 **빈 배열**일 때의 칩 — 자리를 비우지 않고 **눈에 띄게** 그린다.
 *
 * 서버 `schema.prisma`의 `platforms` 주석이 그 상태를 **결함**이라 적는다: 필터 조회의 조건절
 * (`platforms && ARRAY[…]`)이 이 컬럼을 읽으므로 **빈 배열인 행은 필터된 조회에서 영영 빠진다**
 * (「아무에게도 안 보인다」가 의도라면 빈 배열이 아니라 `[INTERNAL]`로 적는 것이 규약이다).
 * 그런 행을 세는 화면은 **여기뿐**이므로(저작은 레포 파일이 소유하고 DB는 파생이다) 자리를 비우면
 * 그 신호가 통째로 사라진다 — 「칩이 없다」와 「조회가 깨졌다」가 구별되지 않는다.
 *
 * ⚠️ **「빈 값 폴백」으로 읽고 `''`로 바꾸지 말 것 — 이것은 없는 값을 «꾸미는» 것이 아니라 결함을
 * «보이게» 하는 것이다.** 빈 문자열이면 알약만 남아 「칩이 없다」와 구별되지 않고, 자리를 아예 비우면
 * 그 행이 **멀쩡한 행으로 보인다**. 판정 근거는 이 파일이 아니라 위에 가리킨 schema 주석에 있다.
 *
 * 그래서 색도 surface 팔레트와 **일부러 다르다**(solid 알약이 아니라 **외곽선 + red**) — 어느 surface
 * 라벨로도 오독되지 않아야 한다. ⚠️ 회색(`bg-gray-100 text-gray-800`)은 이 화면에서 **이미 셋을
 * 뜻한다**: `ALL`의 칩 색 · {@link platformColor}의 모르는 값 폴백(**같은 값**) · `ARCHIVED` 상태 칩.
 * 그래서 회색으로 두면 바로 옆 「전체」 칩과 구별되지 않아 **가장 눈에 안 띄는 색**이 된다.
 * 위 {@link platformLabel} raw 폴백과는 **다른 축**이다: 폴백은 「값은 있는데 사본이 낡아 라벨이
 * 없다」, 이쪽은 **「값이 하나도 없다」**다.
 *
 * 탐지기: `__tests__/releaseNotesPlatformChips.test.tsx`의 「[되돌림] 빈 집합도 자리를 비우지 않는다」.
 * ⚠️ 그 케이스는 **라벨만** 단언한다 — 색은 아무도 재지 않으므로(팔레트 단언은 리팩터마다 깨진다)
 * 이 상수를 바꿔도 spec은 초록이다. 바꾸려면 위 세 문단을 읽고 판단할 것.
 */
const EMPTY_PLATFORMS_LABEL = '(없음)';
// ⚠️ **회색으로 「되돌리지」 말 것** — 운영자 화면 관례가 회색이라 그렇게 보이지만, 이 화면에서
// `bg-gray-100 text-gray-800`은 **이미 셋을 뜻한다**: `ALL`의 칩 색 · `platformColor`의 모르는 값
// 폴백(**같은 값**) · `ARCHIVED` 상태 칩. 그래서 회색이면 바로 옆 「전체」 칩과 구별되지 않아
// **결함을 보이게 하려는 목적의 반대**가 된다(센 근거는 위 JSDoc이 정본이고, spec은 색을 재지 않는다).
const EMPTY_PLATFORMS_CLASS = 'border border-red-300 bg-white text-red-700';

const STATUS_COLORS: Record<Status, string> = {
  DRAFT: 'bg-yellow-100 text-yellow-800',
  PUBLISHED: 'bg-green-100 text-green-800',
  ARCHIVED: 'bg-gray-100 text-gray-800',
};

/**
 * 한 장의 크기 — **서버 `limit` 상한과 같은 값이고, 그것이 고른 이유다.**
 *
 * ⚠️ **이 값을 내리지 말 것.** 서버 `RELEASE_NOTES_LIST_MAX_LIMIT`(= 100)의 JSDoc이 「유일한
 * 호출부(이 파일)가 **정확히 이 값**을 보낸다 … 그래서 짝 spec의 `[대조군] 상한 그대로(MAX)는
 * 통과한다`가 **실제 호출부의 값**을 재고 있다」고 적는다 — 여기서 줄이면 그 문장과 그 대조군이
 * 조용히 거짓이 된다(응답 형상이 같아 어느 검사도 안 깨진다). 올리는 것은 **400이 된다**
 * (`@Max(RELEASE_NOTES_LIST_MAX_LIMIT)` — 여유분이 0이다).
 *
 * 상한을 그대로 쓰는 이유: 이 화면의 목적이 **오래된 노트를 찾아 숨기는 것**이라 장수가 적을수록
 * 좋다(2026-10-20 prod 1500여 건 = 15장). 「한 화면에 100행은 많다」는 축은 여기서 다루지 않는다 —
 * 그러려면 날짜 범위 필터가 먼저이고 그것은 서버 쿼리를 넓히는 별 작업이다.
 */
const PAGE_SIZE = 100;

/**
 * **서버 조회를 좁히는 행의 칸** — 변이 뒤 「제자리 갱신이냐 첫 장 재조회냐」를 이 집합이 가른다.
 *
 * 서버 `findAll`의 `where`가 읽는 칸 그대로다(`release-notes.service.ts`): `status` · **`platforms`**
 * (⚠️ 단수 `platform`이 아니다 — 필터는 `platforms: { has: … }` **집합 축**이고 단수는 대표값이다) ·
 * `category`. 그 칸을 바꾼 행은 **필터가 켜져 있으면 목록 밖으로 나가므로** 제자리로 고칠 수 없다.
 *
 * ⚠️ **필터를 하나 더 만들면 이 배열에 더하라 — 안 더하면 조용히 틀린다.** 그 어긋남은 화면이
 * 「숨겼는데 목록에 남아 있다」로 보이고 응답 형상이 같아 어느 검사도 안 깨진다. 짝으로 아래
 * `activeFilters`가 `Record<(typeof FILTERED_FIELDS)[number], string>`이라 **여기 값을 더하면 tsc가
 * 그 칸을 요구한다** — 그것이 이 집합의 유일한 래칫이다(그래서 두 자리를 따로 손열거하지 말 것).
 */
const FILTERED_FIELDS = ['status', 'platforms', 'category'] as const satisfies readonly (keyof ReleaseNote)[];
// ⚠️ 검색어(`q`)는 이 배열에 **없다 — 빠뜨린 것이 아니다.** 서버가 검색으로 보는 칸은 `title`·
// `content`·`commitHash`이고, 이 화면의 쓰기 둘(`status`·`audiences`)은 **그 칸을 바꾸지 않으므로**
// 검색이 켜져 있어도 변이한 행은 목록 밖으로 나가지 않는다(제자리 패치가 참이다). 내용 축의 쓰기가
// 생기면(= 저작 UI가 돌아오면) 그 칸들을 이 배열에 더해야 한다.

/**
 * 검색 입력 디바운스(ms) — 타건마다 요청이 나가지 않게 한다. 300은 admin 선례
 * (`IndustryWorkCategories.tsx`·`components/OrgTargetCombobox.tsx`)와 같은 값이다.
 */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * 검색어 길이 상한 — 서버 `RELEASE_NOTES_SEARCH_MAX_LENGTH`(= 100)의 **사본**이다(admin은 서버 DTO를
 * import할 수 없다). 넘기면 서버가 **400**이라 입력 칸에서 먼저 막는다. 서버 값을 바꾸면 함께 바꿀 것.
 */
const SEARCH_MAX_LENGTH = 100;

/**
 * 업데이트 내역 **관리** — **조회 + 공개 여부 전환 + 노출 대상뿐이다. 저작 UI는 2026-10-14에 지웠다.**
 *
 * ## ⚠️ 이 화면은 `system-settings` 권한 뒤에 있다 (2026-10-21, 결정 채택 ④)
 * 서버 `AdminReleaseNotesController`가 클래스 레벨 `@RequirePermission('system-settings')`를 들고,
 * `components/Layout.tsx`의 라우트 표가 **같은 키**를 든다(어긋나면 메뉴는 보이는데 403만 받는다).
 * 수퍼관리자가 **자기에게 보이는** 내역을 읽는 자리는 **다른 화면**이다 — `ReleaseNoteFeed.tsx`
 * (권한 요구 없음·읽기만). ⇒ **여기에 읽기 전용 소비 기능을 더하지 말 것**이고, 거꾸로 **그쪽에
 * 숨김·복구·노출 대상을 더하지 말 것**(그러면 이 게이트를 우회하는 입구가 된다).
 *
 * 지운 것: 「＋ 새 릴리즈 노트」·「수정」·「삭제」·「선택 병합」과 그 모달 둘. 서버 라우트
 * (`POST /`·`PATCH :id`·`DELETE :id`·`POST merge`·`auto`·`auto-bulk`)도 함께 사라졌다.
 *
 * **노트의 정본은 레포의 `release-notes/notes/YYYY-MM-DD.json` 파일**이고 DB는 파생이다 —
 * 반영은 `.github/workflows/import-release-notes.yml`(push: master · paths `release-notes/notes/**`)이
 * 한다. 사유 전문은 결정 기록
 * `docs/decisions/2026-10-14-release-notes-authoring-canonical-source-and-popup.md` §②.
 *
 * ⚠️ 그래서 화면에 **안내 배너가 짝으로 필수다** — 운영자가 「수정 버튼이 없어졌다」에서 막히면
 * 그것은 기능 상실이다. 배너는 ① 어디서 저작하나 ② **숨김은 여전히 된다**를 말한다. 저작 UI를
 * 되살리지 않고 이 배너를 지우지 말 것.
 */
export default function ReleaseNotes() {
  const { showToast } = useToastStore();
  const [notes, setNotes] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  /**
   * 다음 장의 커서 — `null`이면 **마지막 장**이고 「더 보기」를 **렌더하지 않는다**(비활성이 아니다).
   *
   * 서버가 `take: limit + 1`로 한 행 더 읽어 `hasMore`를 판정하고 마지막 행의 id를 준다
   * (`release-notes.service.ts` `findAll`). 즉 이 값의 유무가 「더 있나」의 정본이라 행 수로
   * 추측하지 않는다.
   */
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  // 필터
  const [filterStatus, setFilterStatus] = useState<Status | ''>('');
  const [filterPlatform, setFilterPlatform] = useState<Platform | ''>('');
  const [filterCategory, setFilterCategory] = useState<Category | ''>('');

  /**
   * 검색 — 입력값(`searchInput`)과 **조회에 쓰는 값**(`searchQuery`, 디바운스·trim 뒤)을 가른다.
   *
   * 조회는 `searchQuery`로만 한다 — 그것이 아래 effect의 dep이라 **다른 필터와 같은 입구**를 지난다:
   * 세대 가드(늦은 응답이 덮지 않는다)·커서 초기화(`loadFirstPage`가 비운다)·`reloadToken` 재조회가
   * 그대로 적용된다. 입력값을 dep에 넣으면 글자마다 요청이 나가고 세대만 계속 올라간다.
   * 서버 측 검색이어야 하는 이유: 목록이 100건씩이라 받아 온 장만 거르면 그 아래 노트는 영영 안
   * 걸린다(2026-10-28 운영자가 2026-04 노트를 그렇게 못 찾았다).
   */
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // 선택
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  /**
   * **지금 화면에 있는** 선택 — 행에 작용하는 자리는 **전부 이 집합을 읽는다**(목록 순서 그대로).
   *
   * ## 왜 `selectedIds`를 직접 읽지 않나
   * 필터를 바꿔도 선택은 **남는다**(아래 「보존」 문단). 그래서 `selectedIds`에는 **지금 목록에 없는
   * 행**이 섞여 있을 수 있고, 그것을 그대로 서버로 보내면 **운영자가 본 적 없는 행이 숨겨진다** —
   * 게다가 변이 뒤 `patchLoadedNotes`는 **로드된 목록만** 패치하므로 그 행에는 **화면 피드백조차
   * 없다**(「복구」 버튼은 있지만 운영자가 **무엇을 되돌려야 하는지 모른다**).
   *
   * ⭐ 근거는 이 파일에 **이미 판정돼 있다** — 날짜 머리말 체크박스가 「더 보기」 뒤에 풀리는 이유가
   * 같은 문장이다: **「운영자가 본 적 없는 행이 일괄 숨김 대상이 되면 안 된다」**(아래 그 `input`
   * 주석). 두 자리가 같은 원리를 쓰므로 **한쪽만 되돌리지 말 것** — 교집합을 「과하다」며 지우면 그
   * 판정과 모순된다.
   *
   * ## 「필터 변경 시 선택 리셋」을 고르지 않은 이유
   * 리셋은 필터를 **되돌렸을 때 선택을 잃는다**(운영자가 다시 고르는 일이 이 화면의 본 작업이다).
   * 교집합은 **잃는 것이 0**이고 — 같은 화면이 날짜 체크박스에서 이미 그 거래를 택했다 —
   * 「보이는 것 = 작용 대상」을 그 자리에서 참으로 만든다.
   * 탐지기: `__tests__/releaseNotesPaging.test.tsx`의 「필터를 바꾼 뒤」 describe
   * (`[대조군] 화면 밖 선택은 보존된다`가 리셋 구현을 red로 만드는 짝이다).
   *
   * ⚠️ **이 값을 읽는 자리가 셋이고, 하나만 고치면 「숫자는 맞고 라벨만 거짓」이 된다**: 일괄 쓰기
   * (`handleBulkStatus`) · 「선택 노출 대상」 ids · **액션바 게이트와 「N개 선택됨」**. 종전 결함이
   * 정확히 그 비대칭이었다(쓰기 하나는 전체를, 다른 하나는 교집합을 봤다).
   * ⚠️ **「선택 해제」는 예외로 `selectedIds`를 통째로 비운다** — 그것은 행에 **작용**하지 않고 의도를
   * 비우는 탈출구라, 화면 밖 선택까지 지우는 것이 맞다(교집합으로 바꾸면 화면 밖 선택을 **지울 길이
   * 없어진다**).
   */
  const visibleSelectedIds = notes.filter((n) => selectedIds.has(n.id)).map((n) => n.id);

  /**
   * 노출 대상 편집 대상 — `null`이면 모달이 닫혀 있다.
   *
   * 단일 행과 일괄(선택 N건) 둘 다 이 상태 하나로 연다(서버 라우트가 일괄 형상이라 `ids`가 1개여도
   * 같은 길이다). `initial`은 **첫 건의 값**이고 저장은 전부 그 값으로 교체다 — 모달이 그 사실을
   * 문구로 말한다(합집합·교집합으로 보여주면 「무엇이 저장될지」가 화면과 어긋난다).
   */
  const [audienceTarget, setAudienceTarget] = useState<{
    ids: string[];
    initial: string[];
  } | null>(null);

  /**
   * 목록 행의 노출 대상 칩이 읽는 후보 — **라벨 해소에만** 쓴다(선택은 모달이 한다).
   *
   * ⚠️ `enabled`를 켠 채 둔다. 칩은 모달과 무관하게 떠 있고, 후보를 못 받으면 `audienceLabel`이
   * **raw 참조**(`company:owner`)를 그대로 보여 준다 — 그것이 의도다(가리면 운영자가 「대상이 없다」고
   * 믿는다). 두 조회는 `staleTime: 60_000`으로 캐시되므로 행마다 요청이 가지 않는다.
   */
  const { groups: audienceGroups } = useAudienceOptions(true);

  /**
   * 요청 세대 — **느린 앞 응답이 뒤에 도착해 최신 상태를 덮거나 섞는 것**을 막는다.
   *
   * ## 요청원이 둘이고 **다르게 깨진다**
   * 선례는 web의 같은 화면(`apps/web/src/pages/release-notes/index.tsx`의 `requestGenerationRef`
   * JSDoc이 이 논거의 정본)이고, 여기는 필터가 셋이라 더 잘 난다.
   * - **필터 전환**(effect): 앞 응답이 {@link loadFirstPage}의 `setNotes`로 **덮는다** — 최신 필터의
   *   목록이 사라진다.
   * - **「더 보기」**(핸들러): 앞 필터로 띄운 요청이 `setNotes((prev) => [...prev, …])`로 **덧붙는다**
   *   — 한 목록에 **두 필터의 행이 섞인다.** 이쪽이 더 나쁘다(덮는 것은 새로고침하면 맞지만 섞인
   *   목록은 그 자리에서 거짓이고, 그 행을 골라 **일괄 숨김**을 누르는 화면이다). `nextCursor`도 앞
   *   필터의 것으로 밀려 그 뒤 「더 보기」가 엉뚱한 장을 청한다.
   * - 핸들러에는 cleanup이 없으므로 effect용 `alive` 플래그로는 **둘을 함께 막을 수 없다** —
   *   세대 카운터 하나가 한 불변식을 한 자리에서 집행한다.
   *
   * ⚠️ `setLoadingMore(false)`는 **세대를 보지 않는다** — 보게 하면 필터 전환으로 낡아진 더 보기
   * 요청이 그 값을 영구히 `true`로 남겨 버튼이 「불러오는 중...」으로 **잠긴다**.
   * 반대로 `setLoading(false)`는 **보아야 한다**: 낡은 응답이 스피너를 걷으면 최신 요청이 아직 도는
   * 동안 **옛 목록이 「다 불러왔다」는 모습으로** 보인다.
   */
  const requestGenerationRef = useRef(0);

  /**
   * 재조회 신호 — 변이 뒤 **첫 장을 다시 받으라고 effect에 알린다**(핸들러가 직접 받지 않는다).
   *
   * ## 위 {@link requestGenerationRef}와 **다른 축이다 — 서로를 대체하지 못한다**
   * - **세대 카운터**: 「**어느 응답이 상태를 쓰나**」. 요청 자체는 **보낸 시점의 필터로 옳게** 나갔고,
   *   느린 앞 응답이 뒤 응답을 덮거나 섞는 것을 막는다.
   * - **이 토큰**: 「**요청이 어떤 필터로 나가나**」. 핸들러의 클로저는 **클릭 시점의** `filterStatus`·
   *   `filterPlatform`·`filterCategory`를 쥐고 있어서, 변이가 **도는 동안** 운영자가 필터를 바꾸면
   *   그 클로저가 {@link loadFirstPage}를 **옛 필터로** 호출한다.
   *
   * ⚠️ **그 요청은 세대 카운터가 원리적으로 못 잡는다** — `loadFirstPage`가 **스스로 세대를 올리므로
   * 자기가 최신이 된다**(`isStale()`이 `false`다). 그래서 옛 필터의 목록이 **최신 필터의 목록을 덮고**,
   * 드롭다운은 새 필터를 보이는데 행은 옛 필터의 것인 상태가 된다 — 그 행을 골라 **일괄 숨김**을 누르는
   * 화면이라 그 자리에서 거짓이다(그 복합이 사전 결함 ⓐ·ⓑ가 함께 닫혀야 하는 이유였다).
   * 토큰을 쓰면 요청을 보내는 것이 **effect의 새 클로저**이므로 필터가 낡을 수 없다.
   *
   * ⚠️ 거꾸로 **세대 카운터를 이 토큰으로 대체할 수도 없다**: 필터를 빠르게 두 번 바꾸면 effect가 띄운
   * 두 요청이 경합하고 토큰은 그것을 보지 않는다. 그리고 {@link handleLoadMore}는 **커서를 든 사용자
   * 행위**라 effect로 옮길 수 없어 **세대 축이 단독으로 소유하는 자리가 실재한다**.
   *
   * ⚠️ `patchLoadedNotes`의 **분기 판정**(`activeFilters`)은 여전히 낡은 클로저로 돈다 — 그래도
   * 안전한 이유는 **그 판정이 틀려도 요청은 새 필터로 나가기** 때문이다: 재조회로 가면 effect가 현재
   * 필터로 받고, 제자리 패치로 가면 필터 전환이 띄운 effect 요청이 목록을 이미 교체한다. 그러니 이것을
   * ref로 「고치지」 말 것 — 얻는 것 없이 두 축이 섞인다.
   */
  const [reloadToken, setReloadToken] = useState(0);

  /**
   * 첫 장 — 목록을 **교체**한다(필터 전환·변이 후 재조회가 쓴다).
   *
   * `nextCursor`를 **요청 전에** 비운다 — 비우지 않으면 응답이 오기 전 한순간 「더 보기」가 **앞
   * 필터의 커서**를 들고 떠 있다.
   *
   * ⚠️ **그 한 줄에는 오늘 소유자가 없다 — 심층 방어다**(2026-10-20 프로브 실측). 그 축이 보일 창은
   * 「요청을 보냈고 응답이 아직 안 온」 사이뿐인데, 그때 `loading`이 목록을 **「더 보기」 버튼까지
   * 통째로** 스피너로 갈아 버려(아래 `loading ? … : …`) **관측이 원리적으로 불가능하다** — 그래서
   * 짝 spec의 어느 프로브도 이 줄에 반응하지 않는다. ⇒ 지우지 말 것(지워도 아무것도 red가 되지
   * 않는다). 되살아날 조건은 **그 스피너가 목록을 가리지 않게 바뀌는 날**이고, 그때 이 줄이 없으면
   * 운영자가 앞 필터의 커서로 다음 장을 청하게 된다.
   */
  const loadFirstPage = async () => {
    const generation = ++requestGenerationRef.current;
    const isStale = () => generation !== requestGenerationRef.current;
    try {
      setLoading(true);
      setNextCursor(null);
      const response = await adminApi.getReleaseNotes({
        status: filterStatus || undefined,
        platform: filterPlatform || undefined,
        category: filterCategory || undefined,
        q: searchQuery || undefined,
        limit: PAGE_SIZE,
      });
      if (isStale()) return;
      if (response.data) {
        setNotes(response.data.items);
        setNextCursor(response.data.nextCursor);
        setTotal(response.data.total);
      }
    } catch (error) {
      if (isStale()) return;
      showToast(extractApiError(error).message, 'error');
    } finally {
      // 낡은 응답은 스피너를 걷지 않는다(위 JSDoc)
      if (!isStale()) setLoading(false);
    }
  };

  /**
   * 「더 보기」 — 다음 장을 받아 **이어 붙인다**(교체가 아니다).
   *
   * 날짜 그룹핑이 `notes` 배열 전체를 `reduce`하므로 이어 붙이면 그룹이 자연히 자란다.
   *
   * ## id로 **중복을 거른다** — ⚠️ **전제가 바뀠다. 지금은 심층 방어다**
   * 이 필터를 넣은 근거는 「서버 정렬이 비유일하다」였다 — `findAll`이 `orderBy: { releasedAt:
   * 'desc' }` **단독**이고 커서가 `{ id }`라, 동시각 타이가 실재하므로(prod 실측 2026-10-20:
   * 1,522행 / distinct `released_at` **1,330** / 최대 타이 **30** / 타이 그룹 **62**) 장 경계가 타이
   * 그룹 안에 떨어지면 같은 행이 두 장에 올 수 있었다.
   * **그 축은 같은 날 서버에서 닫혔다** — `eb8001ff0`이 `orderBy: [{ releasedAt: 'desc' },
   * { id: 'desc' }]`로 타이브레이크를 넣었다. ⇒ **「원래 필요 없었다」로 읽지 말 것**: 이 줄이 들어온
   * 시점에 그 정렬은 비유일했고, 지금 남아 있는 이유는 **아래 두 문장**이다.
   *
   * **① 두 축은 다른 것이다.** 타이 결함(서버 소유)과 「React가 중복 `key`를 만나면 **그 아래 DOM이
   * 옳은지 모르게 된다**」(이 파일 소유)는 별개다 — 중복이 어디서 오든(서버 회귀·import 중복·미래의
   * 다른 커서) 이 화면은 **같은 노트에 체크박스를 둘** 그리면 안 된다(일괄 숨김 화면에서 그것은
   * 거짓이다). ⚠️ 실측으로는 React가 행을 **생략하지 않고 둘 다 그렸다**(짝 spec P4) — 「조용히
   * 생략된다」고 단정해 적지 말 것.
   * **② 서버의 그 타이브레이크를 재는 검사가 「경계에서 실제로 안 난다」를 증명하지 못한다.**
   * 그쪽 유닛은 `orderBy` 배열을 인자로 단언할 뿐이고 **prisma mock은 `orderBy`를 해석하지 않는다**
   * (그 사실은 `findAll`의 그 자리 주석이 적고 있고, 실 DB 통합 케이스는 **오늘 없다**).
   *
   * ⚠️ 그러니 **이 dedupe의 초록을 「중복이 오지 않는다」의 근거로 인용하지 말 것** — 오늘 그 문장의
   * 소유자는 **서버 `orderBy`**다. 반대로 이 줄을 지우려면 위 ①을 먼저 반박해야 한다.
   */
  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    // 세대를 **지금** 집어 둔다 — 응답이 오기 전에 필터가 바뀌면 이 장은 다른 필터의 것이 된다
    const generation = requestGenerationRef.current;
    try {
      setLoadingMore(true);
      const response = await adminApi.getReleaseNotes({
        status: filterStatus || undefined,
        platform: filterPlatform || undefined,
        category: filterCategory || undefined,
        // 「더 보기」도 같은 검색어를 싣는다 — 빠지면 다음 장이 **검색 밖** 행으로 채워진다
        q: searchQuery || undefined,
        limit: PAGE_SIZE,
        cursor: nextCursor,
      });
      if (generation !== requestGenerationRef.current) return;
      if (response.data) {
        const page = response.data.items;
        setNotes((prev) => {
          const seen = new Set(prev.map((n) => n.id));
          return [...prev, ...page.filter((n) => !seen.has(n.id))];
        });
        setNextCursor(response.data.nextCursor);
        // 필터된 전체 건수는 매 장마다 다시 온다 — 그 사이 import가 돌았으면 그 값이 최신이다
        setTotal(response.data.total);
      }
    } catch (error) {
      showToast(extractApiError(error).message, 'error');
    } finally {
      // 세대를 **보지 않는다** — 위 JSDoc의 경고: 보게 하면 버튼이 영구히 잠긴다
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    void loadFirstPage();

    // 언마운트·필터 재전환이 모두 세대를 올려 진행 중 응답을 「낡은 것」으로 만든다.
    // ⚠️ 값을 지역 변수로 복사하면 이 카운터는 거꾸로 **동작하지 않는다** — 노드를 가리키는 ref가
    //    아니라 **공유 카운터**라 cleanup이 최신 값을 읽는 것이 맞다.
    //    (admin eslint에는 react-hooks 플러그인이 없어 그 경고 자체가 뜨지 않는다 — web 짝 파일은
    //     같은 자리에 `eslint-disable react-hooks/exhaustive-deps`를 달고 있다.)
    return () => {
      requestGenerationRef.current++;
    };
    // `reloadToken`이 **재조회의 유일한 입구**다 — 변이 핸들러가 `loadFirstPage()`를 직접 부르면
    // 그 클로저의 **옛 필터**로 요청이 나간다(그 상수 JSDoc). 그래서 이 dep을 지우지 말 것.
  }, [filterStatus, filterPlatform, filterCategory, searchQuery, reloadToken]);

  /**
   * 지금 켜져 있는 필터 — 칸 이름이 {@link FILTERED_FIELDS}와 **같아야** 한다(타입이 강제한다).
   *
   * `platforms`에 **단수** 상태(`filterPlatform`)를 넣는 것이 맞다 — 운영자가 고르는 것은 값 하나이고
   * 서버가 그것으로 **집합**을 좁힌다(`platforms: { has: … }`). 칸 이름을 `platform`으로 바꾸면 위
   * 집합과 어긋나 래칫이 풀린다.
   */
  const activeFilters: Record<(typeof FILTERED_FIELDS)[number], string> = {
    status: filterStatus,
    platforms: filterPlatform,
    category: filterCategory,
  };

  /**
   * 변이 뒤 목록 갱신 — **누적 로드를 잃지 않는다.**
   *
   * ## 왜 매번 첫 장부터 다시 받지 않나
   * 그러면 15장을 넘긴 운영자가 **위치를 통째로 잃는다.** 이 화면의 목적이 「오래된 노트를 찾아
   * 숨기는 것」인데(2026-10-20 운영자가 6개월 전 노트에서 막혔다) 숨기는 순간 목록이 최근 100건으로
   * 되돌아가면 그 일이 반쯤만 된다. 그리고 선택(`selectedIds`)이 **화면에 없는 행을 가리키게** 된다.
   *
   * ## 제자리로 고칠 수 있는 조건 — 그 칸이 조회를 좁히지 않을 때
   * 두 쓰기 응답은 `{ updated: number }`뿐이라 행을 돌려주지 않는다. 그래서 **보낸 값으로** 패치하는데,
   * 그 칸이 {@link FILTERED_FIELDS}에 있고 그 필터가 켜져 있으면 그 행은 **목록 밖으로 나가야 하므로**
   * 제자리 패치가 거짓이 된다(숨겼는데 「게시됨」 필터에 그대로 남아 보인다). 그때만 첫 장을 다시 받는다.
   *
   * ⚠️ `total`은 건드리지 않는다 — 필터된 **전체 건수**라 이 패치로 바뀌지 않는다(바뀌는 경우는 위
   * 재조회 분기로 가고 거기서 서버가 새 값을 준다).
   *
   * @param updated 서버가 **실제로 바꾼 건수**(`{ updated: number }`). 응답 봉투가 비면 `undefined`가
   *   오고 그때도 재조회로 떨어진다(fail-closed).
   */
  const patchLoadedNotes = (
    ids: string[],
    patch: Partial<ReleaseNote>,
    updated: number | undefined,
  ) => {
    /*
      ⚠️ **「항상 패치」로 단순화하지 말 것 — 분기가 둘이고 이유가 다르다.**

      ① 보낸 수와 바꾼 수가 다르면(`updated !== ids.length`) **보낸 값으로 패치하면 거짓이 된다** —
         일부만 바뀠는데 **전부** 바뀐 것처럼 그린다. 응답은 「몇 건」만 주고 「**어느** 건」을 주지
         않으므로(서버 `bulkUpdateStatus`가 `updateMany(...).count`를 돌려준다) 그 행을 고를 수가
         없다. 그래서 서버에 다시 묻는 것이 **참을 아는 유일한 길**이다. 실제로 갈리는 경우: DB는
         레포 노트 파일에서 파생되므로 화면에 뜬 뒤 그 행이 사라질 수 있고, `where: { id: { in } }`은
         없는 id를 **조용히 건너뛴다**(에러가 아니다).
      ② 바꾼 칸이 서버 조회를 좁히는 칸이고 그 필터가 켜져 있으면 그 행은 **목록 밖으로 나가야**
         하므로 제자리 패치가 거짓이 된다(숨겼는데 「게시됨」 필터에 그대로 남아 보인다).
    */
    // ⚠️ 두 분기 모두 `loadFirstPage()`를 **직접 부르지 않는다** — 이 함수는 변이 핸들러의 클로저에서
    //    돌고 그 클로저의 필터 상태는 **클릭 시점의 것**이라, 변이가 도는 동안 필터가 바뀌었으면
    //    옛 필터로 받아 **최신 목록을 덮는다**(사유·두 축의 구분은 `reloadToken` JSDoc).
    if (updated !== ids.length) {
      setReloadToken((t) => t + 1);
      return;
    }
    const leavesTheFilter = FILTERED_FIELDS.some(
      (field) => field in patch && activeFilters[field] !== '',
    );
    if (leavesTheFilter) {
      setReloadToken((t) => t + 1);
      return;
    }
    const idSet = new Set(ids);
    setNotes((prev) => prev.map((n) => (idSet.has(n.id) ? { ...n, ...patch } : n)));
  };

  /**
   * 공개 여부 전환 — 단일 항목도 **일괄 엔드포인트**(`ids: [id]`)를 쓴다.
   *
   * 종전엔 `updateReleaseNote(id, { status })`(= `PATCH :id`)였다. 그 라우트는 제목·내용도 받았으므로
   * 저작과 함께 지웠고, 남은 쓰기는 `bulk-status` 하나다 — 그래서 **제목을 바꿀 수 있는 요청 형상이
   * 아예 존재하지 않는다**(규율이 아니라 형상이 막는다).
   */
  const handleStatusChange = async (id: string, status: VisibilityStatus) => {
    try {
      const response = await adminApi.bulkUpdateReleaseNoteStatus([id], status);
      showToast(`상태가 ${withDirectionParticle(STATUS_LABELS[status])} 변경되었습니다.`, 'success');
      patchLoadedNotes([id], { status }, response.data?.updated);
    } catch (error) {
      showToast(extractApiError(error).message, 'error');
    }
  };

  /**
   * 선택 일괄 전환 — 대상은 {@link visibleSelectedIds}다(`selectedIds` 전체가 **아니다**).
   *
   * ⚠️ `Array.from(selectedIds)`로 되돌리지 말 것 — 필터를 바꾸면 선택이 남으므로 그 집합에는 **지금
   * 목록에 없는 행**이 섞이고, 그러면 운영자가 **본 적 없는 행이 숨겨지는데 화면 피드백도 없다**
   * (사유 전문은 그 파생값 JSDoc).
   */
  const handleBulkStatus = async (status: VisibilityStatus) => {
    // 선택을 **비우기 전에** 집어 둔다 — 아래 패치 대상이 이 목록이다
    const ids = visibleSelectedIds;
    if (ids.length === 0) return;
    try {
      const response = await adminApi.bulkUpdateReleaseNoteStatus(ids, status);
      showToast(`${ids.length}개 항목이 ${STATUS_LABELS[status]} 상태가 되었습니다.`, 'success');
      /*
        ⚠️ **`new Set()`으로 되돌리지 말 것 — 작용한 id만 뺀다.**
        대상이 교집합이 된 뒤로 「전부 비우기」는 **작용하지 않은 화면 밖 선택을 조용히 버리는 것**이
        된다(운영자는 필터를 되돌려야 비로소 알아차린다). 화면 밖 선택이 없는 경우에는 두 구현이
        **같은 결과**라, 갈리는 것은 정확히 이 결함의 경우뿐이다.
        비우는 길이 막히는 것도 아니다 — 「선택 해제」가 통째로 비운다(그 자리 주석).
      */
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      patchLoadedNotes(ids, { status }, response.data?.updated);
    } catch (error) {
      showToast(extractApiError(error).message, 'error');
    }
  };

  /**
   * 노출 대상 저장 — **전체 교체**다(더하기·빼기가 아니다).
   *
   * 빈 배열이 「모두에게」로 되돌리는 요청이므로 그 경우도 그대로 보낸다 — 거르면 설정을 해제할
   * 길이 없어진다. 성공 문구가 「모두에게」와 「N개 역할」을 **가른다**: 운영자가 방금 무엇을 했는지
   * 토스트만 보고 알 수 있어야 한다(빈 배열을 「대상 없음」으로 읽으면 뜻이 정반대가 된다).
   *
   * ⚠️ **셋째 인자(승인)는 2026-10-20에 들어왔다** — 서버가 「닿는데 좁히지 않은 체계」를 400으로
   * 거부하게 됐고, 그 거부를 받아 줄 자리가 모달의 확인란이다. 이 함수는 그 값을 **번역하지 않고
   * 전달만** 한다. 거부 분류 둘(`AUDIENCE_NAMESPACE_UNCONSTRAINED` = 승인으로 통과 /
   * `…_UNREACHABLE` = 승인 경로 없음)의 정본은 서버의 `ReleaseNoteAudienceProblem` JSDoc이다.
   */
  const handleSaveAudiences = async (
    ids: string[],
    audiences: string[],
    // ⚠️ **그대로 전달한다 — `?? false`를 붙이지 말 것.** 기본값의 소유자는 서버 하나이고
    //    (그쪽이 fail-closed로 `false`를 쓴다) 여기서 번역하면 기본값이 두 곳이 된다. 그 어긋남은
    //    응답 형상이 같아 조용하다. 이 값이 무엇을 승인하는지는 모달의 `needsAcknowledgement` JSDoc.
    acknowledgeUnconstrained?: boolean,
  ) => {
    try {
      const response = await adminApi.bulkUpdateReleaseNoteAudiences(
        ids,
        audiences,
        acknowledgeUnconstrained,
      );
      showToast(
        audiences.length === 0
          ? `${ids.length}개 항목이 모두에게 보이도록 설정되었습니다.`
          : `${ids.length}개 항목의 노출 대상이 ${audiences.length}개 역할로 설정되었습니다.`,
        'success',
      );
      setAudienceTarget(null);
      // `audiences`는 {@link FILTERED_FIELDS}에 **없다** — 서버 조회를 좁히지 않으므로 필터 축으로는
      // 늘 제자리다(노출 대상 필터가 생기면 그 배열에 더해야 하고, 그러면 이 호출이 자동으로 재조회로
      // 갈린다). ⚠️ 그래도 `updated` 축은 **그대로 적용된다** — 보낸 수와 바꾼 수가 다르면 재조회다.
      patchLoadedNotes(ids, { audiences }, response.data?.updated);
    } catch (error) {
      // ⚠️ 모달을 닫지 않는다 — 접두 없는 값·권한 실패로 400/403이 오면 고친 선택을 잃지 않게.
      showToast(extractApiError(error).message, 'error');
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // 날짜별 그룹핑 (KST 기준)
  const groupedNotes = notes.reduce(
    (acc, note) => {
      const date = toKSTDateString(note.releasedAt);
      if (!acc[date]) acc[date] = [];
      acc[date].push(note);
      return acc;
    },
    {} as Record<string, ReleaseNote[]>,
  );

  const sortedDates = Object.keys(groupedNotes).sort((a, b) => b.localeCompare(a));

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        {/*
          제목에 「관리」가 붙는 이유 — 사이드바 라벨과 같아야 한다(`Layout.tsx`의 그 경로 항목).
          2026-10-21에 소비 뷰가 생기며 **전제가 바뀠다**: 그때까지 이 화면이 유일해 제목이
          모호하지 않았지만, 이제 두 화면의 h1이 같으면 사이드바 활성 표시 없이는 어느 쪽인지
          알 수 없다(dev 실측에서 드러났다).
        */}
        <h1 className="text-2xl font-bold">업데이트 내역 관리</h1>
      </div>

      {/*
        안내 배너 — 저작 UI를 지운 짝이다(컴포넌트 JSDoc 참조). 「수정 버튼이 어디 갔나」에
        화면에서 답해야 한다: ① 저작은 레포 파일 ② 숨김·복구는 여기서 그대로 된다.
      */}
      <div className="mb-4 p-4 bg-brand-50 border border-brand-200 rounded-card text-sm text-brand-900 space-y-1">
        <p className="font-medium">
          업데이트 내역은 이 화면에서 작성·수정하지 않습니다 — 정본은 레포의 노트 파일입니다.
        </p>
        <p>
          새 노트나 문구 수정은{' '}
          <code className="px-1 py-0.5 bg-white/70 rounded">release-notes/notes/날짜.json</code>{' '}
          파일을 고쳐 master에 push하면 자동으로 반영됩니다. 이 화면에서 고치면 그 파일과 내용이
          갈리기 때문에 저작 기능을 두지 않았습니다.
        </p>
        <p className="font-medium">
          단, 틀린 내용을 즉시 내리는 것은 여기서 됩니다 — 「비공개」를 누르면 사용자 화면에서 바로
          사라지고, 「게시」로 되돌릴 수 있습니다.
        </p>
      </div>

      {/* 필터 */}
      <div className="flex gap-4 mb-4">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as Status | '')}
          className="border rounded-control px-3 py-2"
        >
          <option value="">모든 상태</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          value={filterPlatform}
          onChange={(e) => setFilterPlatform(e.target.value as Platform | '')}
          className="border rounded-control px-3 py-2"
        >
          {/*
            「모든 플랫폼」은 **필터 없음**(빈 값)이다 — 유산 값 `ALL`과 한국어로 구별되지 않아
            그 옵션을 뺐다({@link LEGACY_SURFACES}). 집합 축에서 `ALL`은 와일드카드가 아니라 원소다.
          */}
          <option value="">모든 플랫폼</option>
          {PLATFORM_FILTER_OPTIONS.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value as Category | '')}
          className="border rounded-control px-3 py-2"
        >
          <option value="">모든 카테고리</option>
          {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        {/*
          검색 — 서버 측(`q`)이다. 다른 필터와 **교집합**이고 「총 N개」도 검색 결과 수가 된다
          (서버 `count`가 같은 `where`를 센다). placeholder는 이름이 아니므로 aria-label을 따로 둔다.
        */}
        <input
          type="search"
          aria-label="업데이트 내역 검색"
          placeholder="제목·내용·커밋 해시 검색"
          maxLength={SEARCH_MAX_LENGTH}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="border rounded-control px-3 py-2 min-w-[240px]"
        />
        {/*
          ⚠️ **「총 N개」만 적지 말 것 — 한 장만 받아 두고 그 말을 하면 거짓이다.** 종전에는 100행을
          받아 놓고 「총 1500개」라 적었다(숫자는 서버가 준 **필터된 전체 건수**로 맞고 **라벨만**
          거짓이라 tsc·spec이 원리적으로 못 잡는 종류다 — 운영자에겐 「1500건이 여기 다 있다」로
          읽혀 없는 노트를 찾아 헤매게 된다).
          다 받은 뒤에는 「중 M개」를 떼어 잡음을 남기지 않는다 — 그때는 두 수가 같아 「총 N개」가 참이다.
        */}
        <span className="text-gray-500 self-center">
          {notes.length < total ? `총 ${total}개 중 ${notes.length}개 표시` : `총 ${total}개`}
        </span>
      </div>

      {/*
        선택 액션 — 게이트와 카운트가 **버튼이 실제로 보낼 집합**({@link visibleSelectedIds})을 읽는다.

        ⚠️ `selectedIds.size`로 되돌리지 말 것 — 그러면 「5개 선택됨」이라 적고 2건만 보내는 상태가
        된다. **숫자는 서버가 준 것도 아니고 라벨만 거짓인 종류**라 tsc·응답 형상 단언이 원리적으로
        못 잡고, 이 화면은 같은 종류의 거짓 라벨(「총 N개」)을 2026-10-20에 이미 한 번 고쳤다.
        카운트가 0이면 바가 사라지는 것도 **옳다** — 화면 밖 선택만 남은 상태에서 누를 버튼이 없다
        (게이트를 전체로 되돌리면 바가 「0개 선택됨」으로 떠 있고 그 버튼들은 눌러도 **조용히 아무
        일도 하지 않는다** — `handleBulkStatus`가 빈 목록에서 즉시 반환한다). 그 선택을 비우려면
        필터를 되돌려 바를 띄우면 되고(선택은 보존된다) 그것이 유일한 경로다.
        ⚠️ **게이트와 카운트는 다른 축이다** — 보이는 선택이 1건 이상이면 바는 **어느 구현에서도**
        떠 있어 카운트 케이스가 이 축을 재지 못한다. 게이트의 단독 소유자는
        `__tests__/releaseNotesPaging.test.tsx`의 **「[되돌림] 화면 밖 선택만 남으면 액션바가
        사라진다」**다(프로브 실측 P-A4 — 그 표가 그 spec에 있다).
      */}
      {visibleSelectedIds.length > 0 && (
        <div className="flex gap-2 mb-4 p-3 bg-brand-50 border border-brand-200 rounded-card">
          <span className="self-center text-brand-800">{visibleSelectedIds.length}개 선택됨</span>
          {/*
            숨김과 복구를 **둘 다** 둔다 — 종전엔 「선택 게시」만 있어 여러 건을 한 번에 내리려면
            한 건씩 눌러야 했다. 급히 내려야 할 때가 이 화면이 존재하는 이유다.
          */}
          <Button variant="secondary" size="sm" onClick={() => handleBulkStatus('ARCHIVED')}>
            선택 비공개
          </Button>
          <Button variant="success" size="sm" onClick={() => handleBulkStatus('PUBLISHED')}>
            선택 게시
          </Button>
          {/*
            일괄 노출 대상 — `initial`은 **선택 중 첫 건**의 값이다(목록 순서 기준). 합집합으로
            보여주지 않는 이유는 저장이 「전부 그 값으로 교체」라 화면과 어긋나기 때문이고, 모달이
            그 사실을 경고 문구로 말한다.
          */}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              // 이 자리는 **처음부터 교집합을 취하고 있었다** — 그래서 공용 파생값으로 바꾸는 것이
              // 동작 변경이 아니다(같은 식의 사본이었다). 「첫 건」도 그 배열의 첫 원소라 목록 순서
              // 기준이 그대로 유지된다.
              const first = notes.find((n) => n.id === visibleSelectedIds[0]);
              setAudienceTarget({ ids: visibleSelectedIds, initial: first?.audiences ?? [] });
            }}
          >
            선택 노출 대상
          </Button>
          {/*
            ⚠️ **여기만 `selectedIds`를 통째로 비운다 — 교집합으로 바꾸지 말 것.** 이 버튼은 행에
            **작용하지 않고** 의도를 비우므로 화면 밖 선택까지 지우는 것이 맞고, 교집합으로 좁히면
            화면 밖 선택을 **지울 길이 없어진다**(필터를 일일이 되돌려야 한다).
          */}
          <Button variant="secondary" size="sm" onClick={() => setSelectedIds(new Set())}>
            선택 해제
          </Button>
        </div>
      )}

      {/* 목록 */}
      {loading ? (
        <div className="text-center py-8 text-gray-500">로딩 중...</div>
      ) : notes.length === 0 ? (
        // 검색 중의 0건은 「내역이 없다」가 아니라 「검색어에 맞는 것이 없다」다 — 같은 문구면 운영자가
        // 목록이 비었다고 읽는다.
        <div className="text-center py-8 text-gray-500">
          {searchQuery
            ? `「${searchQuery}」에 맞는 업데이트 내역이 없습니다.`
            : '업데이트 내역이 없습니다.'}
        </div>
      ) : (
        <div className="space-y-6">
          {sortedDates.map((date) => (
            <div key={date} className="border rounded-card overflow-hidden">
              <div className="bg-gray-100 px-4 py-2 font-medium flex items-center gap-2">
                {/*
                  날짜 전체 선택 — 뜻은 「**지금 화면에 있는** 그 날짜 행 전부가 선택됨」이다.

                  ⚠️ 그래서 「더 보기」로 **같은 날짜 행이 더 들어오면 이 체크가 풀린다. 그것이 옳다**
                  (2026-10-20 누적 로드와 함께 판정): 체크가 남으면 「전부 선택됨」이 그 자리에서
                  거짓이 되고, 반대로 새 행을 **자동 선택**하면 **운영자가 본 적 없는 행이 일괄 숨김
                  대상**이 된다. 선택 자체(`selectedIds`)는 그대로 보존되므로 **잃는 것은 0**이다.
                  ⇒ 「풀리는 게 버그처럼 보인다」며 `every`를 「하나라도 선택됨」으로 바꾸거나 새 장의
                  행을 자동 선택하지 말 것.
                  ⭐ **같은 원리가 한 자리 더 있다** — {@link visibleSelectedIds}(일괄 작업이 화면 밖
                  행을 건드리지 않는 이유). 둘 중 하나만 되돌리면 그쪽과 모순된다. 탐지기는
                  `__tests__/releaseNotesPaging.test.tsx`의 「[되돌림] 같은 날짜 행이 더 오면 날짜
                  체크박스가 풀린다」다.
                */}
                <input
                  type="checkbox"
                  checked={groupedNotes[date].every((n) => selectedIds.has(n.id))}
                  onChange={() => {
                    const dateIds = groupedNotes[date].map((n) => n.id);
                    const allSelected = dateIds.every((id) => selectedIds.has(id));
                    const newSelected = new Set(selectedIds);
                    dateIds.forEach((id) => {
                      if (allSelected) {
                        newSelected.delete(id);
                      } else {
                        newSelected.add(id);
                      }
                    });
                    setSelectedIds(newSelected);
                  }}
                  className="mr-2"
                />
                {date}
                <span className="text-gray-500 text-sm">({groupedNotes[date].length}개)</span>
              </div>
              <div className="divide-y">
                {groupedNotes[date].map((note) => (
                  <div
                    key={note.id}
                    className={`px-4 py-3 flex items-center gap-3 hover:bg-gray-50 ${
                      selectedIds.has(note.id) ? 'bg-brand-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(note.id)}
                      onChange={() => toggleSelect(note.id)}
                    />
                    <span className={`px-2 py-0.5 text-xs rounded ${STATUS_COLORS[note.status]}`}>
                      {STATUS_LABELS[note.status]}
                    </span>
                    {/*
                      surface **집합** — 원소마다 칩을 하나 둔다(`note.platform` 단수를 읽지 말 것).
                      ⚠️ 목록 필터는 집합 축이라(서버 `platforms: { has: … }`) 대표값 하나만 그리면
                      「앱」으로 걸러 받은 행이 「웹」 칩으로 보인다 — 운영자에게는 그것이 「필터가
                      고장났다」로 읽힌다(prod 실측 `ADMIN` 축 108건, 분모 1506행).
                      web(`apps/web/src/pages/release-notes/index.tsx`)은 같은 집합을 **한 줄로**
                      (`' · '` join + 첫 값 색) 그린다 — 거기는 여러 노트의 **그룹 머리말**이고 색이
                      text-only라 그 형태가 맞다. 여기는 **행마다** 그리는 `bg-*` 알약이라 join하면
                      색이 대표값으로 남으므로 값마다 하나를 둔다(라벨·색 둘 다 집합이 된다).
                      ⚠️ key에 index를 섞는다 — 중복 원소(`[WEB, WEB]`)는 import 결함이지만 **올 수
                      있고**, 값만 key로 쓰면 React가 중복 key에서 칩을 조용히 생략해 그 결함이
                      화면에서 사라진다(그러면 탐지기가 아니라 은폐다).
                    */}
                    <span className="flex items-center gap-1">
                      {note.platforms.length === 0 ? (
                        <span className={`px-2 py-0.5 text-xs rounded ${EMPTY_PLATFORMS_CLASS}`}>
                          {EMPTY_PLATFORMS_LABEL}
                        </span>
                      ) : (
                        note.platforms.map((p, i) => (
                          <span
                            key={`${p}-${i}`}
                            className={`px-2 py-0.5 text-xs rounded ${platformColor(p)}`}
                          >
                            {platformLabel(p)}
                          </span>
                        ))
                      )}
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded ${CATEGORY_COLORS[note.category]}`}>
                      {CATEGORY_LABELS[note.category]}
                    </span>
                    <span className="flex-1 font-medium">{note.title}</span>
                    {/*
                      노출 대상 — **빈 배열에는 아무것도 그리지 않는다.** 그것이 「모두에게」(기본값)
                      이고 1500여 건 전량이 그 상태라, 칩을 띄우면 모든 행에 의미 없는 배지가 붙어
                      ⚠️ 진짜 설정된 행이 눈에 안 띈다. `platforms`의 빈 배열과 **처분이 반대**인
                      이유가 그것이다(그쪽은 결함이라 빨간 `(없음)`을 띄운다 — 위 그 상수 JSDoc).
                      라벨 해소는 `audienceLabel`이 하고, 후보에 없는 참조는 **raw 값 그대로** 보인다
                      (가리면 운영자가 「대상이 비어 있다」고 믿는데 서버는 그 제약으로 거른다).
                    */}
                    {note.audiences.length > 0 && (
                      <span className="flex items-center gap-1">
                        {note.audiences.map((ref, i) => (
                          <span
                            key={`${ref}-${i}`}
                            className="px-2 py-0.5 text-xs rounded border border-violet-300 bg-violet-50 text-violet-800"
                            title={ref}
                          >
                            {audienceLabel(ref, audienceGroups)}
                          </span>
                        ))}
                      </span>
                    )}
                    {note.version && (
                      <span className="text-gray-500 text-sm">v{note.version}</span>
                    )}
                    {/*
                      버튼은 하나씩만 뜬다 — 「게시됨」이면 비공개, 그 밖이면(ARCHIVED·옛 DRAFT)
                      게시다.
                      ⚠️ `ARCHIVED`를 이 조건에서 빼지 말 것 — 종전에는 `DRAFT`와 `PUBLISHED`만
                      버튼을 가져서 **숨긴 노트에 아무 버튼도 없었다**(복구가 「선택 게시」로만
                      가능했다). 숨김이 되돌릴 수 없으면 그것은 숨김이 아니다.
                    */}
                    <div className="flex gap-1">
                      {/*
                        노출 대상 편집 — 상태 버튼과 **같은 묶음**에 둔다(둘 다 「누가 보나」를 바꾸는
                        축이고 서버에서도 쓰기 둘이 형제다). 상태와 달리 **항상 뜬다** — 숨김 상태의
                        노트도 대상을 고쳐 둘 수 있어야 복구 뒤에 맞는 사람에게 간다.
                      */}
                      <button
                        onClick={() =>
                          setAudienceTarget({ ids: [note.id], initial: note.audiences })
                        }
                        className="px-2 py-1 text-xs bg-violet-50 text-violet-700 border border-violet-200 rounded hover:bg-violet-100"
                      >
                        노출 대상
                      </button>
                      {note.status === 'PUBLISHED' ? (
                        <button
                          onClick={() => handleStatusChange(note.id, 'ARCHIVED')}
                          className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                        >
                          비공개
                        </button>
                      ) : (
                        <button
                          onClick={() => handleStatusChange(note.id, 'PUBLISHED')}
                          className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                        >
                          게시
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/*
            「더 보기」 — `nextCursor`가 `null`이면 **렌더하지 않는다**(비활성이 아니라 없앤다).
            「존재가 상태를 뜻하는 요소는 조건부 렌더」가 이 레포 관례이고, web 짝 화면
            (`apps/web/src/pages/release-notes/index.tsx`)도 같은 형태다.
            ⚠️ 무한 스크롤로 바꾸지 말 것 — 여기는 **체크박스로 행을 골라 숨기는** 화면이라 스크롤이
            로드를 유발하면 고르는 중에 목록이 움직인다(2026-10-20 팀장 결정).
          */}
          {nextCursor && (
            <div className="text-center pt-2 pb-4">
              <Button variant="secondary" onClick={handleLoadMore} disabled={loadingMore}>
                {loadingMore ? '불러오는 중...' : '이전 업데이트 내역 더 보기'}
              </Button>
            </div>
          )}
        </div>
      )}

      {/*
        생성/수정 모달과 병합 모달은 2026-10-14에 지웠다 — 저작은 레포의 노트 파일이 소유한다
        (컴포넌트 JSDoc). ⚠️ 2026-10-19에 모달이 **하나** 생겼다: 노출 대상(역할)이다.
        그것이 「저작 되살리기」가 아닌 이유는 **고치는 축이 DB 소유인 것 하나뿐**이기 때문이고
        (`audiences` — 노트 파일 스키마에 없다) 사유 전문은 서버
        `AdminReleaseNotesController.bulkUpdateAudiences` JSDoc이다. 제목·내용·분류·날짜·`platforms`를
        받는 모달을 **다시 만들지 말 것** — 그 축은 노트 파일을 고쳐 push하는 일이다.
      */}
      {audienceTarget && (
        <ReleaseNoteAudienceModal
          noteIds={audienceTarget.ids}
          initialAudiences={audienceTarget.initial}
          multiple={audienceTarget.ids.length > 1}
          onClose={() => setAudienceTarget(null)}
          onSave={(audiences, acknowledgeUnconstrained) =>
            handleSaveAudiences(audienceTarget.ids, audiences, acknowledgeUnconstrained)
          }
        />
      )}
    </div>
  );
}
